import { c as _c } from "react/compiler-runtime";
import { useUser } from "@auth0/nextjs-auth0/client";
import type { AccountCustomerOutputDto } from "@sticker-giant/sg-frontend-common";
import { useEffect, useRef } from "react";
import { identifyAuthenticatedCustomer, identifyUser, resetIdentityIfStale } from "@/lib/analytics/segment";
import { useCustomer } from "@/queries/useCustomer";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";

// What already went out for a customer. `withPii: false` records the id-only fallback and
// is deliberately NOT terminal — on signup the account record is frequently unreadable on
// the first lookup, and the traits this hook exists to send still have to go out when it
// lands. Treating the fallback as final stranded those sessions on an id-only identify.
type SentIdentity = {
  customerId: string;
  withPii: boolean;
};

// Sends the right identify for this customer and reports what went out, or undefined when
// nothing should be sent — either the account request is still in flight (wait for the
// traits rather than sending an id we'd have to follow up on) or we're already done.
type SendIdentityInput = {
  customerId: string;
  sent: SentIdentity | undefined;
  customer?: AccountCustomerOutputDto;
  accountFailed?: boolean;
};
function sendIdentity({
  customerId,
  sent,
  customer,
  accountFailed
}: SendIdentityInput): SentIdentity | undefined {
  const alreadySent = sent?.customerId === customerId;
  // Only a PII identify closes this out; an id-only fallback stays open to upgrade.
  if (alreadySent && sent?.withPii) return undefined;
  if (customer) {
    identifyAuthenticatedCustomer(customerId, customer);
    return {
      customerId,
      withPii: true
    };
  }

  // The account request is exhausted — fall back to the id alone rather than leaving the
  // session unattributed, but only once per customer.
  if (accountFailed && !alreadySent) {
    identifyUser(customerId);
    return {
      customerId,
      withPii: false
    };
  }
  return undefined;
}

// Keeps Segment's identity a pure projection of the authenticated user:
// - logged in  -> identify the customer with their PII, so every event after login/signup
//                 carries hashed identifiers the ad platforms can match on
// - logged out -> reset, but only if an identity is actually persisted
// - still loading, or claims not yet hydrated -> wait (avoids resetting a valid user)
//
// Exactly one identify goes out per login: we hold it until the account request settles
// rather than sending the id first and the traits second. The cost is that identity lands
// one round-trip later; the account service is the only source of name and phone.
export function useSyncSegmentIdentity() {
  const $ = _c(7);
  const {
    user,
    isLoading
  } = useUser();
  const customerId = useAuthClaimsStore(_temp);
  const {
    data: customer,
    isError
  } = useCustomer();
  const sent = useRef(undefined);
  let t0;
  let t1;
  if ($[0] !== customer || $[1] !== customerId || $[2] !== isError || $[3] !== isLoading || $[4] !== user) {
    t0 = () => {
      if (isLoading) {
        return;
      }
      if (!user) {
        sent.current = undefined;
        resetIdentityIfStale();
        return;
      }
      if (!customerId) {
        return;
      }
      const next = sendIdentity({
        customerId,
        sent: sent.current,
        customer,
        accountFailed: isError
      });
      if (next) {
        sent.current = next;
      }
    };
    t1 = [isLoading, user, customerId, customer, isError];
    $[0] = customer;
    $[1] = customerId;
    $[2] = isError;
    $[3] = isLoading;
    $[4] = user;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
}
function _temp(state) {
  return state.claims?.sg_customer_id;
}